import type { PermitResponse } from '@contracts/permitSearch/PermitListResponse';
import type { GridApi } from '@mui/x-data-grid-pro';
import { Button } from '@src/common/components/button/Button';
import { Stack } from '@src/common/components/Stack';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { useToast } from '@src/common/hooks/useToast';
import { CsvExporter } from '@src/csvFiles/CsvExporter';
import { PermitTypeFormatter } from '@src/permits/permitSearch/PermitTypeFormatter';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  permits: PermitResponse[];
  gridApiRef: React.MutableRefObject<GridApi>;
}

export const CsvExportButton = ({ permits, gridApiRef }: Props) => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const getSortedPermits = (): PermitResponse[] => {
    const datagridSortedRowIds = gridApiRef.current
      .getSortedRowIds()
      .map((id) => String(id));

    const permitsLookupMap = new Map(permits.map((p) => [p.id, p]));

    return datagridSortedRowIds
      .map((id) => permitsLookupMap.get(id))
      .filter((p): p is PermitResponse => p !== undefined);
  };

  const data = getSortedPermits().map((permit) => ({
    submissionDate: permit.submissionDate?.slice(0, 10),
    jurisdiction: permit.type.jurisdiction,
    type: PermitTypeFormatter.format(permit.type.type),
    name: permit.type.name,
    agent: permit.claimedBy,
    client: permit.client.name,
    stateConfirmationNumber: permit.stateId,
    statePermitNumber: permit.statePermitNumber,
    stateFees: permit.fees?.jurisdictionFee?.amount,
    stateFeesCurrency: permit.fees?.jurisdictionFee?.currency,
    stateFeesWaived: permit.fees?.jurisdictionFee.isWaived ? 'true' : 'false',
    plateNumber: permit.plateNumber,
    vin: permit.vin,
    unitNumber: permit.unitNumber,
    invoiceNumber: permit.invoiceNumber,
    billTo: permit.billTo?.name,
    serviceFees: permit.fees?.serviceFee.amount,
    serviceFeesCurrency: permit.fees?.serviceFee?.currency,
    serviceFeesWaived: permit.fees?.serviceFee.isWaived ? 'true' : 'false',
    status: permit.status.toLocaleLowerCase(),
  }));
  const columns = [
    { key: 'submissionDate', header: t('permitSearch.submissionDate') },
    { key: 'jurisdiction', header: t('permitSearch.jurisdiction') },
    { key: 'type', header: t('permitSearch.type') },
    { key: 'name', header: t('permitSearch.name') },
    { key: 'agent', header: t('permitSearch.agent') },
    { key: 'client', header: t('permitSearch.client') },
    {
      key: 'stateConfirmationNumber',
      header: t('permitSearch.stateConfirmationNumber'),
    },
    { key: 'statePermitNumber', header: t('permitSearch.statePermitNumber') },
    { key: 'stateFees', header: t('permitSearch.stateFees') },
    { key: 'stateFeesCurrency', header: t('permitSearch.stateFeesCurrency') },
    { key: 'stateFeesWaived', header: t('permitSearch.stateFeesWaived') },
    { key: 'plateNumber', header: t('permitSearch.plateNumber') },
    { key: 'vin', header: t('permitSearch.vin') },
    { key: 'unitNumber', header: t('permitSearch.unitNumber') },
    { key: 'invoiceNumber', header: t('permitSearch.invoiceNumber') },
    { key: 'billTo', header: t('fields.billTo') },
    { key: 'serviceFees', header: t('permitSearch.serviceFees') },
    {
      key: 'serviceFeesCurrency',
      header: t('permitSearch.serviceFeesCurrency'),
    },
    { key: 'serviceFeesWaived', header: t('permitSearch.serviceFeesWaived') },
    { key: 'status', header: t('permitSearch.status') },
  ];

  const onClick = async () => {
    try {
      await CsvExporter.exportData(data, columns);
    } catch {
      showToast({
        severity: ToastLevel.error,
        message: t('permitSearch.exportCsvError'),
      });
    }
  };

  return (
    <Stack alignItems="flex-start">
      <Button size="small" variant="contained" onClick={onClick}>
        {t('permitSearch.exportCsv', {
          quantity: permits.length,
        })}
      </Button>
    </Stack>
  );
};
